
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, Check, Calendar } from 'lucide-react';
import { useBookingRealtimeSync } from '@/hooks/useBookingRealtimeSync';

interface BookingData {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  format: string;
  service: {
    service_name: string;
  } | null;
  practitioner: {
    first_name: string | null;
    last_name: string | null;
  } | null;
}

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const [isLoading, setIsLoading] = useState(true);
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleBookingUpdate = (updatedBooking: any) => {
    if (updatedBooking.stripe_session_id === sessionId) {
      setBookingData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: updatedBooking.status
        };
      });
    }
  };

  useBookingRealtimeSync({
    userId: user?.id,
    type: 'client',
    onBookingUpdate: handleBookingUpdate
  });

  useEffect(() => {
    const fetchBookingData = async () => {
      if (!sessionId && !bookingId) {
        setIsLoading(false);
        return;
      }

      // Handle case where user is logged out but we still have booking params
      if (!user) {
        console.log('User logged out but booking params available. Attempting session recovery...');
        
        // Simplified session recovery with better error handling
        try {
          // Try to get current session with proper waiting
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (session && !error) {
            console.log('Session recovered successfully after payment redirect');
            // Session recovered, let the auth context handle it
            // Give time for auth context to update
            setTimeout(() => {
              setIsLoading(false);
            }, 1000);
            return;
          }
          
          // Enhanced session recovery for paid bookings
          const preservedUserId = localStorage.getItem('payment_session_user_id') || sessionStorage.getItem('payment_session_user_id');
          const preservedBookingId = localStorage.getItem('payment_session_booking_id') || sessionStorage.getItem('payment_session_booking_id');
          const preservedTimestamp = localStorage.getItem('payment_session_timestamp') || sessionStorage.getItem('payment_session_timestamp');
          const preservedToken = localStorage.getItem('payment_session_session_token') || sessionStorage.getItem('payment_session_session_token');
          const preservedRefreshToken = localStorage.getItem('payment_session_refresh_token') || sessionStorage.getItem('payment_session_refresh_token');
          
          // Add timestamp validation to prevent stale data
          const now = Date.now();
          const sessionTime = preservedTimestamp ? parseInt(preservedTimestamp) : 0;
          const maxAge = 60 * 60 * 1000; // 60 minutes for paid bookings
          
          if (preservedUserId && (preservedBookingId || sessionId || bookingId) && 
              (now - sessionTime) < maxAge) {
            console.log('Found valid preserved session info from payment');
            
            // Try to restore the session if we have tokens
            if (preservedToken && preservedRefreshToken) {
              try {
                console.log('Attempting to restore session with preserved tokens');
                const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                  access_token: preservedToken,
                  refresh_token: preservedRefreshToken
                });
                
                if (sessionData?.session && !sessionError) {
                  console.log('Successfully restored session after payment redirect');
                  // Give time for auth context to update
                  setTimeout(() => {
                    setIsLoading(false);
                  }, 1000);
                  return;
                }
              } catch (restoreError) {
                console.error('Failed to restore session:', restoreError);
              }
            }
            
            // Fallback: Try to fetch booking data even without auth context
            const { data, error: bookingError } = await supabase
              .from('bookings')
              .select(`
                id, 
                start_time, 
                end_time, 
                status, 
                format,
                stripe_session_id,
                service:service_id (service_name),
                practitioner:practitioner_id (first_name, last_name)
              `)
              .eq('client_id', preservedUserId)
              .eq(preservedBookingId ? 'id' : sessionId ? 'stripe_session_id' : 'id', 
                  preservedBookingId || sessionId || bookingId)
              .limit(1);
              
            if (!bookingError && data && data.length > 0) {
              console.log('Found booking with preserved session info:', data[0]);
              setBookingData(data[0] as any);
            }
          }
          
          // Clean up old session info after use
          const sessionKeys = [
            'payment_session_user_id', 'payment_session_booking_id', 'payment_session_user_email',
            'payment_session_timestamp', 'payment_session_session_token', 'payment_session_refresh_token',
            'payment_session_expires_at'
          ];

          sessionKeys.forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          });

          // Also clean up booking flow session storage
          sessionStorage.removeItem('tempBookingInfo');
          sessionStorage.removeItem('bookingInfo');
          sessionStorage.removeItem('booking_choice_made');
          sessionStorage.removeItem('booking_filters');
          
        } catch (error) {
          console.error('Session recovery failed:', error);
        }
        
        console.log('Showing booking success without user session');
        setIsLoading(false);
        return;
      }

      try {
        // Enhanced booking data fetching with better error handling for free sessions
        let query = supabase
          .from('bookings')
          .select(`
            id, 
            start_time, 
            end_time, 
            status, 
            format,
            stripe_session_id,
            service:service_id (service_name),
            practitioner:practitioner_id (first_name, last_name)
          `)
          .eq('client_id', user.id);

        if (sessionId) {
          console.log('Fetching booking by session_id:', sessionId);
          query = query.eq('stripe_session_id', sessionId);
        } else if (bookingId) {
          console.log('Fetching booking by booking_id:', bookingId);
          query = query.eq('id', bookingId);
        }

        const { data, error } = await query.limit(1);

        if (error) {
          console.error('Error fetching booking on success page:', error);
        } else if (data && data.length > 0) {
          console.log('Found booking data:', data[0]);
          setBookingData(data[0] as any);

          // Clean up booking flow session storage after successful booking
          sessionStorage.removeItem('tempBookingInfo');
          sessionStorage.removeItem('bookingInfo');
          sessionStorage.removeItem('booking_choice_made');
          sessionStorage.removeItem('booking_filters');
        } else {
          console.log('No matching booking found. Checking for recent bookings...');
          
          // Fallback: try to find the most recent booking for this user
          // This helps with free sessions that might not have stripe_session_id
          const { data: recentData, error: recentError } = await supabase
            .from('bookings')
            .select(`
              id, 
              start_time, 
              end_time, 
              status, 
              format,
              stripe_session_id,
              service:service_id (service_name),
              practitioner:practitioner_id (first_name, last_name)
            `)
            .eq('client_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1);

          if (!recentError && recentData && recentData.length > 0) {
            console.log('Using most recent booking:', recentData[0]);
            setBookingData(recentData[0] as any);

            // Clean up booking flow session storage after successful booking
            sessionStorage.removeItem('tempBookingInfo');
            sessionStorage.removeItem('bookingInfo');
            sessionStorage.removeItem('booking_choice_made');
            sessionStorage.removeItem('booking_filters');
          } else {
            console.log('No bookings found for user');
          }
        }
      } catch (error) {
        console.error('Error in booking success page:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingData();
  }, [sessionId, bookingId, user]);

  // Separate effect for redirect timer that depends on bookingData
  useEffect(() => {
    if (!user || !bookingData) return;

    console.log('Setting up redirect timer for authenticated user with booking data');
    const redirectTimer = window.setTimeout(() => {
      console.log('Auto-redirecting to client dashboard with upcoming tab');
      navigate('/client-dashboard?tab=upcoming', { replace: true });
    }, 5000);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [user, bookingData, navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow flex items-center justify-center bg-amber-50/50 py-10 px-4">
        <Card className="max-w-md w-full p-8 shadow-lg">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="h-10 w-10 text-amber-600 animate-spin mb-4" />
              <p className="text-lg text-earth-800">Confirming your booking...</p>
            </div>
          ) : (
            <div className="text-center">
              <div className="mx-auto bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mb-6">
                <Check className="h-10 w-10 text-green-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-earth-900 mb-2">
                Booking Confirmed!
              </h1>
              
              <p className="text-earth-700 mb-6">
                {user ? 'Your appointment has been successfully booked.' : 'Your appointment has been confirmed. Please log in to view your bookings.'}
              </p>
              
              {bookingData && user && (
                <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                  <p className="font-medium text-earth-800">
                    {bookingData.service?.service_name || 'Appointment'}
                  </p>
                  <p className="text-sm text-earth-600 mt-1">
                    with {bookingData.practitioner?.first_name || ''} {bookingData.practitioner?.last_name || ''}
                  </p>
                  <p className="text-sm text-earth-600 mt-1">
                    {new Date(bookingData.start_time).toLocaleDateString()} at {new Date(bookingData.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </p>
                  <p className="text-sm text-earth-600 mt-1">
                    {bookingData.format}
                  </p>
                </div>
              )}
              
              {user ? (
                <>
                  <p className="text-sm text-gray-500 mb-6">
                    Redirecting to your dashboard in a few seconds...
                  </p>
                  
                  <div className="flex flex-col gap-3">
                    <Button 
                      onClick={() => navigate('/client-dashboard?tab=upcoming', { replace: true })}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      View My Appointments
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/book-an-appointment')}
                      className="w-full"
                    >
                      Book Another Appointment
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-3">
                  <Button 
                    onClick={() => navigate('/client-auth')}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                  >
                    Log In to View Appointments
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/book-an-appointment')}
                    className="w-full"
                  >
                    Book Another Appointment
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default BookingSuccess;
